import { useEffect, useRef, useState } from 'react';

import AmbientAudioButton from './AmbientAudioButton.jsx';
import DeleteAccountModal from './DeleteAccountModal.jsx';
import FullscreenButton from './FullscreenButton.jsx';
import Icon from './Icon.jsx';
import { levelTitle } from '../lib/xp.js';
import { openBillingPortal } from '../lib/accountClient.js';

const GAIN_MS = 1500;

/**
 * One line describing where the account stands.
 *
 * A cancelled subscription still has access until the period ends, and saying
 * "Premium" flat would let that end as a surprise. Naming the date is the
 * difference between a renewal someone chose and one they didn't notice.
 */
function planLine(user) {
  if (!user?.premium) return 'Free plan';
  const ends = user.premiumUntil
    ? new Date(user.premiumUntil).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
      })
    : null;
  if (user.cancelAtPeriodEnd) return ends ? `Premium until ${ends}` : 'Premium — cancelled';
  return ends ? `Premium · renews ${ends}` : 'Premium';
}

/**
 * App chrome: identity, streak, level progress and the ambient audio toggle.
 * On narrow screens the XP bar drops to its own row rather than shrinking to
 * an unreadable sliver.
 */
export default function Header({
  streak,
  streakBroken,
  level,
  xp,
  isPremium,
  account,
  audio,
  onUpgrade,
  onSignIn,
}) {
  const pct = Math.round(level.progress * 100);
  const [menuOpen, setMenuOpen] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalProblem, setPortalProblem] = useState(null);
  const [portalUrl, setPortalUrl] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const menuRef = useRef(null);

  // Close the account menu on an outside click or Escape — a menu you can only
  // dismiss by hitting its own button is a menu people get stuck in.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (event) => {
      if (!menuRef.current?.contains(event.target)) setMenuOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  /**
   * Announce XP gains: the bar glows and the amount floats up off it.
   * Keyed on an incrementing id so consecutive gains restart the animation
   * instead of the second one being swallowed because the node never changed.
   */
  const [gain, setGain] = useState(null); // { id, amount }
  const previousXp = useRef(xp);

  useEffect(() => {
    const delta = xp - previousXp.current;
    previousXp.current = xp;
    if (delta <= 0) return undefined;

    setGain((current) => ({ id: (current?.id ?? 0) + 1, amount: delta }));
    const handle = setTimeout(() => setGain(null), GAIN_MS);
    return () => clearTimeout(handle);
  }, [xp]);

  return (
    <header className="header">
      <div className="header__row">
        <div className="header__brand">
          <span className="header__mark">
            <Icon name="cross" size={19} />
          </span>
          <span className="header__title">
            Scripture <em>Journey</em>
          </span>
        </div>

        <div className="header__actions">
          <div
            className={`streak-pill${streakBroken ? ' is-cold' : ''}${
              streak.current > 0 && !streakBroken ? ' is-hot' : ''
            }`}
            title={
              streakBroken
                ? 'Your streak has cooled off — study today to restart it'
                : `Longest streak: ${streak.longest} days`
            }
          >
            <span className="streak-pill__flame">
              <Icon name="flame" size={15} />
            </span>
            <span className="streak-pill__count">{streak.current}</span>
            <span className="streak-pill__label">day{streak.current === 1 ? '' : 's'}</span>
          </div>

          <AmbientAudioButton
            enabled={audio.enabled}
            supported={audio.supported}
            onToggle={audio.toggle}
          />

          {/* Sits with the audio toggle rather than out on its own: they are the
              two controls that change how the app is experienced rather than
              what it does, and neither wants attention. */}
          <FullscreenButton />

          {isPremium ? (
            <span className="badge badge--premium" title="Premium unlocked">
              <Icon name="radiance" size={14} />
              Premium
            </span>
          ) : (
            /* Collapses to a ✦ icon on phones so the wordmark isn't ellipsed;
               the label returns as soon as there's room. */
            <button
              type="button"
              className="btn btn--ghost btn--sm upgrade-btn"
              onClick={onUpgrade}
              aria-label="Upgrade to Premium"
            >
              <span className="upgrade-btn__glyph">
                <Icon name="radiance" size={17} />
              </span>
              <span className="upgrade-btn__label">Upgrade</span>
            </button>
          )}

          {account?.signedIn ? (
            <div className="account-menu" ref={menuRef}>
              {/* data-clarity-mask: session replay records the DOM, and both
                  the tooltip and the panel below carry the customer's email
                  address. Masking here rather than relying on Clarity's
                  dashboard setting, which is one click away from being changed
                  by someone who does not know it is load-bearing. */}
              <button
                type="button"
                className="account-chip"
                data-clarity-mask="true"
                onClick={() => setMenuOpen((open) => !open)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                title={account.user?.email}
              >
                {/* An initial, not a full address: the header is not the place
                    to print someone's email at everyone standing behind them. */}
                {(account.user?.email ?? '?').slice(0, 1).toUpperCase()}
              </button>

              {menuOpen ? (
                <div className="account-menu__panel" role="menu">
                  <p className="account-menu__email" data-clarity-mask="true">
                    {account.user?.email}
                  </p>
                  <p className="account-menu__plan">{planLine(account.user)}</p>

                  {/* Shown whenever there's a Stripe customer, not only while
                      premium: someone whose card failed needs this most. */}
                  {account.user?.hasBilling ? (
                    <button
                      type="button"
                      role="menuitem"
                      disabled={portalBusy}
                      onClick={async () => {
                        setPortalBusy(true);
                        setPortalProblem(null);
                        setPortalUrl(null);
                        try {
                          const { url } = await openBillingPortal();
                          window.location.assign(url);
                          /*
                           * If we are still here a moment later, the navigation
                           * did not happen.
                           *
                           * assign() is called after an await, which on some
                           * mobile browsers is outside the user-gesture window
                           * that permits a navigation — and when it is refused
                           * nothing is thrown and nothing is logged. The button
                           * simply does nothing, which is exactly how this was
                           * reported and is invisible to any test that only
                           * checks the API.
                           *
                           * So offer the link instead. A tap on a real anchor is
                           * a fresh gesture and always allowed.
                           *
                           * Unverified, and honestly so: Chrome will not let a
                           * test stub location.assign, so the refused-navigation
                           * case cannot be reproduced here. It is cheap and it
                           * cannot make anything worse — on a normal redirect
                           * the page is gone long before the timer fires.
                           */
                          setTimeout(() => {
                            setPortalBusy(false);
                            setPortalUrl(url);
                          }, 1200);
                        } catch (error) {
                          // Was a bare `catch {}` that closed the menu and said
                          // nothing, so a failure looked exactly like a button
                          // that did not work. The server sends a real reason;
                          // show it and leave the menu open to be read.
                          setPortalBusy(false);
                          setPortalProblem(
                            error?.message ?? 'Could not open the billing portal.',
                          );
                          if (typeof account.refresh === 'function') account.refresh();
                        }
                      }}
                    >
                      {portalBusy ? 'Opening…' : 'Manage subscription'}
                    </button>
                  ) : null}

                  {portalUrl ? (
                    <a
                      className="account-menu__link"
                      href={portalUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open your billing page →
                    </a>
                  ) : null}

                  {portalProblem ? (
                    <p className="account-menu__problem" role="alert">
                      {portalProblem}
                    </p>
                  ) : null}

                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      account.signOut();
                    }}
                  >
                    Sign out
                  </button>

                  <div className="account-menu__legal">
                    <a href="/terms" target="_blank" rel="noreferrer">
                      Terms
                    </a>
                    <a href="/privacy" target="_blank" rel="noreferrer">
                      Privacy
                    </a>
                  </div>

                  {/*
                    Deleting is irreversible and the confirmation is a password,
                    so it opens a proper dialog rather than living as one more
                    button in a menu where Sign out is a neighbour.
                  */}
                  <button
                    type="button"
                    role="menuitem"
                    className="account-menu__danger"
                    onClick={() => {
                      setMenuOpen(false);
                      setDeleting(true);
                    }}
                  >
                    Delete account
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <button
              type="button"
              className="btn btn--ghost btn--sm signin-btn"
              onClick={onSignIn}
            >
              Sign in
            </button>
          )}
        </div>
      </div>

      <div className="header__xp">
        <div className="xp-meta">
          <span className="xp-level">
            Level {level.level}
            <span className="xp-title">{levelTitle(level.level)}</span>
          </span>
          <span className="xp-numbers">
            {level.into} / {level.needed} XP
          </span>
        </div>
        <div
          className={`xp-track${gain ? ' is-gaining' : ''}`}
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Progress to next level"
        >
          <div className="xp-fill" style={{ width: `${pct}%` }} />
          {gain ? (
            <span key={gain.id} className="xp-gain" aria-hidden="true">
              +{gain.amount} XP
            </span>
          ) : null}
        </div>
      </div>

      {deleting ? (
        <DeleteAccountModal account={account} onClose={() => setDeleting(false)} />
      ) : null}
    </header>
  );
}
